import {
  MenuList,
  MenuItem,
  MenuDivider,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { DeleteFilled, ArrowSplitFilled } from '@fluentui/react-icons';
import { useEffect, useRef } from 'react';

const useStyles = makeStyles({
  menuContainer: {
    position: 'fixed',
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    minWidth: '200px',
  },
});

interface EdgeContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  edgeId: string | null;
  /** Source node ID of the edge (for parallel branch creation) */
  sourceNodeId?: string | null;
  /** Whether this is a scope/dotted edge (parallel branch not applicable) */
  isScopeEdge?: boolean;
  /** Source handle id (e.g., 'true', 'false', 'loop', 'case-X') used to label the menu item correctly. */
  sourceHandle?: string | null;
  onClose: () => void;
  onDelete: () => void;
  onAddParallelBranch?: (sourceNodeId: string) => void;
}

export const EdgeContextMenu = ({
  isOpen,
  position,
  edgeId,
  sourceNodeId,
  isScopeEdge,
  sourceHandle,
  onClose,
  onDelete,
  onAddParallelBranch,
}: EdgeContextMenuProps) => {
  const styles = useStyles();
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !position || !edgeId) return null;

  // Branch handles produce a "scope" edge from a control-flow container into one
  // of its branches. Deleting that edge moves the action out of the branch back
  // to the top level, so use a label that reflects that intent.
  const isBranchEdge =
    sourceHandle === 'true' ||
    sourceHandle === 'false' ||
    sourceHandle === 'loop' ||
    sourceHandle === 'default' ||
    (typeof sourceHandle === 'string' && sourceHandle.startsWith('case-'));

  // For non-branch scope/dotted edges (if any) there's nothing meaningful to do.
  if (isScopeEdge && !isBranchEdge) return null;

  // Parallel branch only applies to regular runAfter edges, not branch edges.
  const showParallelBranch = sourceNodeId && onAddParallelBranch && !isBranchEdge;

  const deleteLabel = isBranchEdge ? 'Move out of branch' : 'Delete Edge';

  return (
    <div
      ref={menuRef}
      className={styles.menuContainer}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <MenuList>
        {showParallelBranch && (
          <>
            <MenuItem
              icon={<ArrowSplitFilled />}
              onClick={() => {
                onAddParallelBranch(sourceNodeId);
                onClose();
              }}
            >
              Add a parallel branch
            </MenuItem>
            <MenuDivider />
          </>
        )}
        <MenuItem
          icon={<DeleteFilled />}
          onClick={() => {
            onDelete();
            onClose();
          }}
        >
          {deleteLabel}
        </MenuItem>
      </MenuList>
    </div>
  );
};
